import { API_BASE_URL } from './apiConfig';

/**
 * Custom error class with status and retryable info.
 */
export class ApiError extends Error {
  constructor(message, status, retryable = false) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.retryable = retryable;
  }
}

const MAX_RETRIES = 2;
const BACKOFF_MS = [1000, 2000];

function isRetryable(status) {
  return status === 503 || status === 502 || status === 504;
}

/**
 * Fetch wrapper with auto-retry on 503/network errors.
 * No retry on 404 or other client errors.
 *
 * @param {string} path - API path (e.g. '/api/blog/posts/my-slug')
 * @param {RequestInit} options - fetch options
 * @returns {Promise<any>} parsed JSON response
 * @throws {ApiError}
 */
export async function apiFetch(path, options = {}) {
  const url = `${API_BASE_URL}${path}`;
  let lastError;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      if (attempt > 0) {
        const delay = BACKOFF_MS[attempt - 1] || 2000;
        console.log(`[apiFetch] Retry ${attempt}/${MAX_RETRIES} after ${delay}ms: ${path}`);
        await new Promise(r => setTimeout(r, delay));
      }

      const response = await fetch(url, options);

      if (response.ok) {
        return await response.json();
      }

      // 404 — permanent, no retry
      if (response.status === 404) {
        let data;
        try { data = await response.json(); } catch { data = {}; }
        throw new ApiError(data.error || 'Not found', 404, false);
      }

      // 503/502/504 — retryable
      if (isRetryable(response.status)) {
        let data;
        try { data = await response.json(); } catch { data = {}; }
        lastError = new ApiError(
          data.error || 'Service temporarily unavailable',
          response.status,
          data.retryable !== false
        );
        // Continue to next retry attempt
        continue;
      }

      // Other errors — not retryable
      let data;
      try { data = await response.json(); } catch { data = {}; }
      throw new ApiError(data.error || `Request failed (${response.status})`, response.status, false);

    } catch (err) {
      if (err instanceof ApiError && !err.retryable) {
        throw err;
      }

      // Network error — retryable
      if (!(err instanceof ApiError)) {
        lastError = new ApiError(err.message || 'Network error', 0, true);
        continue;
      }

      lastError = err;
    }
  }

  // All retries exhausted
  console.warn(`[apiFetch] All retries exhausted for ${path}`);
  throw lastError;
}
